import { i18n } from '@apps/main/modules/localization/localizationSetup';
import { FlightIngredient } from '@apps/main/modules/recipe/ingredients';
import { LabelFormatterType } from '@apps/main/new-desktop/core/components/formatters';
import type { FlightsFilters } from '@apps/main/new-desktop/modules/flightsFilters';
import { FlightsFilterType } from '@apps/main/new-desktop/modules/flightsFilters';

import {
  assertValue,
  milisecondsToHHMM,
  HHMMToMiliseconds,
  getRoundedMinutesFromHours,
} from '@libs/utils';
import { createIngredientId } from '@libs/trip-planner-interfaces';
import type {
  CarrierName,
  DecimalRangeType,
  IngredientId,
  StringRangeType,
} from '@libs/trip-planner-interfaces';

import type {
  CheckboxFilterData,
  RadioButtonFilterData,
  RangeSliderFilterData,
  SliderFilterData,
} from '../../../modules/filters';
import { FilterType, SliderFilterState } from '../../../modules/filters';

const { t } = i18n.global;

const MAX_RANGE_TIME_IN_MS = 86340000;
const HALF_HOUR_IN_MS = 1800000;
const MAX_PRICE_RANGE = 100000;
const PRICE_STEP = 10;

// TODO: Add tests when the feature will be approved
export function getDecimalRangeType(value: number): DecimalRangeType {
  return {
    from: undefined,
    to: value === MAX_PRICE_RANGE ? undefined : value,
  };
}

export function getStringRangeType(range: number[]): StringRangeType {
  const firstValue = range[0];
  const secondValue = range[1];

  assertValue(
    firstValue,
    'flightsListFiltersStoreLogic.ts > getStringRangeType > firstValue'
  );
  assertValue(
    secondValue,
    'flightsListFiltersStoreLogic.ts > getStringRangeType > secondValue'
  );
  return {
    from: firstValue === 0 ? undefined : milisecondsToHHMM(firstValue),
    to:
      secondValue === MAX_RANGE_TIME_IN_MS
        ? undefined
        : milisecondsToHHMM(secondValue),
  };
}

export function getDefaultFilters(): FlightsFilters {
  return {
    ingredientId: createIngredientId(''),
    arrivalTime: {
      filterType: FlightsFilterType.ArrivalTime,
      label: t('filter.flightFilters.arrival'),
      filters: [],
    },
    carriers: {
      filterType: FlightsFilterType.Carriers,
      data: [],
      label: t('filter.flightFilters.carrier'),
    },
    departureTime: {
      filterType: FlightsFilterType.DepartureTime,
      filters: [],
      label: t('filter.flightFilters.departure'),
    },
    numberOfStops: {
      filterType: FlightsFilterType.NumberOfStops,
      filters: [],
      label: t('filter.flightFilters.stops'),
    },
    price: {
      filterType: FlightsFilterType.Price,
      filters: [],
      label: t('filter.flightFilters.price'),
    },
    travelDuration: {
      filterType: FlightsFilterType.TravelDuration,
      filters: [],
      label: t('filter.flightFilters.travel_duration'),
    },
  };
}

export function getCarriersCheckboxFilter(
  carriers: CarrierName[],
  flight: FlightIngredient
): CheckboxFilterData[] {
  return carriers.map((carrier) => ({
    filterType: FilterType.Checkbox,
    ingredientId: flight.ingredientId,
    options: {
      checked: flight.filters.carrierFilters.includes(carrier.code),
      label: carrier.name,
      code: carrier.code,
      type: {
        theme: 'grey',
        size: 'small',
        isDisabled: false,
      },
    },
  }));
}

export function getNumberOfStopsFilter(
  flight: FlightIngredient
): RadioButtonFilterData {
  return {
    filterType: FilterType.RadioButton,
    options: [
      {
        id: 'all',
        active: undefined,
        label: t('filter.flightFilters.all'),
      },
      {
        id: 'direct',
        active: 0,
        label: t('filter.flightFilters.direct_flight_only'),
      },
      {
        id: 'oneStop',
        active: 1,
        label: t('filter.flightFilters.max_one_stop'),
      },
    ],
    selected: flight.filters.maxNumberOfStops,
    theme: 'new',
    title: `${flight.fromPortText} - ${flight.toPortText}`,
    ingredientId: flight.ingredientId,
  };
}

export function getPriceSliderFilter(
  flight: FlightIngredient,
  currency?: string
): SliderFilterData {
  const initialFilterValue = flight.filters.priceFilters?.to || MAX_PRICE_RANGE;
  return {
    filterType: FilterType.Slider,
    state: SliderFilterState.Default,
    options: {
      id: flight.ingredientId,
      initialValue: initialFilterValue,
      min: 0,
      max: MAX_PRICE_RANGE,
      step: PRICE_STEP,
    },
    title: `${flight.fromPortText} - ${flight.toPortText}`,
    labelFormatter: {
      formatterType: LabelFormatterType.MoneyFormatter,
      data: {
        minPrice: 0,
        currency: currency || '',
        price: initialFilterValue,
        color: 'color-neutral-50',
        font: 'font-label-m-light',
      },
    },
  };
}

export function getTravelDurationSliderFilter(
  flight: FlightIngredient,
  from?: number,
  to?: number
): SliderFilterData {
  if (flight && from && to) {
    const initialFilterValue = flight.filters.maxDurationInHours
      ? getRoundedMinutesFromHours(flight.filters.maxDurationInHours)
      : to;

    return {
      filterType: FilterType.Slider,
      state: SliderFilterState.Default,
      options: {
        id: flight.ingredientId,
        initialValue: initialFilterValue,
        min: from,
        max: to,
        step: 5,
      },
      title: `${flight.fromPortText} - ${flight.toPortText}`,
      labelFormatter: {
        formatterType: LabelFormatterType.TravelDurationFormatter,
        data: {
          value: initialFilterValue,
          color: 'color-neutral-50',
          font: 'font-label-m-light',
        },
      },
    };
  } else {
    return {
      filterType: FilterType.Slider,
      state: SliderFilterState.Loading,
      title: `${flight.fromPortText} - ${flight.toPortText}`,
    };
  }
}

export function getArrivalTimeRangeSliderFilter(
  flight: FlightIngredient
): RangeSliderFilterData {
  const initialFilterValue = [
    flight.filters.arrivalTimeFilters?.from
      ? HHMMToMiliseconds(flight.filters.arrivalTimeFilters?.from)
      : 0,
    flight.filters.arrivalTimeFilters?.to
      ? HHMMToMiliseconds(flight.filters.arrivalTimeFilters.to)
      : MAX_RANGE_TIME_IN_MS,
  ];
  return {
    filterType: FilterType.RangeSlider,
    options: {
      id: flight.ingredientId,
      initialValue: initialFilterValue,
      min: 0,
      max: MAX_RANGE_TIME_IN_MS,
      step: HALF_HOUR_IN_MS,
    },
    title: `${flight.fromPortText} - ${flight.toPortText}`,
    labelFormatter: {
      formatterType: LabelFormatterType.TimeRangeFormatter,
      data: {
        value: initialFilterValue,
        color: 'color-neutral-50',
        font: 'font-label-m-light',
      },
    },
  };
}

export function getDepartureTimeRangeSliderFilter(
  flight: FlightIngredient
): RangeSliderFilterData {
  const initialFilterValue = [
    flight.filters.departureTimeFilters?.from
      ? HHMMToMiliseconds(flight.filters.departureTimeFilters?.from)
      : 0,
    flight.filters.departureTimeFilters?.to
      ? HHMMToMiliseconds(flight.filters.departureTimeFilters.to)
      : MAX_RANGE_TIME_IN_MS,
  ];
  return {
    filterType: FilterType.RangeSlider,
    options: {
      id: flight.ingredientId,
      initialValue: initialFilterValue,
      min: 0,
      max: MAX_RANGE_TIME_IN_MS,
      step: HALF_HOUR_IN_MS,
    },
    title: `${flight.fromPortText} - ${flight.toPortText}`,
    labelFormatter: {
      formatterType: LabelFormatterType.TimeRangeFormatter,
      data: {
        value: initialFilterValue,
        color: 'color-neutral-50',
        font: 'font-label-m-light',
      },
    },
  };
}

export function getFlightDurationRangeFromFilters(
  ingredientId: IngredientId,
  filters: FlightsFilters | null
): DecimalRangeType | undefined {
  const flightDurationFilter = filters?.travelDuration.filters.find(
    (filter) => {
      if (filter.state === SliderFilterState.Default) {
        return filter.options.id === ingredientId;
      }
      return false;
    }
  );

  if (
    flightDurationFilter &&
    flightDurationFilter.state === SliderFilterState.Default
  ) {
    return {
      from: flightDurationFilter.options.min,
      to: flightDurationFilter.options.max,
    };
  }
}

export function getFilterKeyFromTagId(tagId: string): string | undefined {
  if (Object.keys(FlightIngredient.defaultFilters).includes(tagId)) {
    return tagId;
  }

  const filterKey = tagId.split('.')[0];

  if (
    filterKey &&
    Object.keys(FlightIngredient.defaultFilters).includes(filterKey)
  ) {
    return filterKey;
  }

  return undefined;
}
